import { useEffect, useRef } from 'react';
import Select, {
  components,
  type GroupBase,
  type GroupHeadingProps,
  type OptionProps,
  type StylesConfig,
} from 'react-select';

interface CustomCheckboxProps {
  isIndeterminate: boolean;
  isChecked: boolean;
  onClick: VoidFunction;
}

const CustomCheckbox = (props: CustomCheckboxProps) => {
  const { isIndeterminate, isChecked, onClick } = props;
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isIndeterminate && !isChecked && ref.current) {
      ref.current.indeterminate = true;
    }
  }, [isIndeterminate]);

  return (
    <div>
      <input
        onChange={() => {}}
        className="scale-110 cursor-pointer"
        ref={ref}
        type="checkbox"
        checked={isChecked}
        onClick={onClick}
      />
    </div>
  );
};

interface OptionType {
  label: string;
  value: string;
  group: string;
}

interface GroupedSelectProps {
  key: string;
  name: string;
  selectedOptions: string[];
  options: OptionType[];
  isLoading: boolean;
  isClearable: boolean;
  placeholder: string;
  noOptionsMessage: string;
  hideSelectedOptions: boolean;
  groups: string[];
  handleChange: (newValues: string[]) => void;
}

const GroupedSelect = (props: GroupedSelectProps) => {
  const {
    key,
    name,
    selectedOptions,
    groups,
    options,
    isLoading,
    isClearable,
    placeholder,
    noOptionsMessage,
    hideSelectedOptions,
    handleChange,
  } = props;

  const customStyles: StylesConfig<OptionType> = {
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '8px',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
    }),
    option: (provided, state) => ({
      ...provided,
      color: 'black',
      background: state.isFocused ? '#DEEBFF' : 'white',
      cursor: 'pointer',
    }),
    valueContainer: (provided, _state) => ({
      ...provided,
      maxHeight: '70px',
      overflowY: 'auto',
    }),
  };

  const groupedOptions = groups.map((group) => {
    return {
      label: group,
      options: options.filter((o) => o.group === group),
    };
  });

  const optionIsSelected = (option: OptionType, group: string) =>
    option.group === group && selectedOptions.includes(option.value);

  const selectAllGroupOptions = (options: readonly OptionType[]) => {
    if (!selectedOptions) {
      handleChange(options.map((opt) => opt.value));
      return;
    }
    const newOptions = Array.from(new Set([...selectedOptions, ...options.map((opt) => opt.value)]));
    handleChange(newOptions);
  };

  const deselectAllGroupOptions = (options: readonly OptionType[]) => {
    if (!selectedOptions) {
      // already deselected ??
      return;
    }
    const excludedIds = options.map((opt) => opt.value);
    const filteredIds = selectedOptions.filter((id) => !excludedIds.includes(id));
    handleChange(filteredIds);
  };

  const CustomOption = (props: OptionProps<OptionType, boolean, GroupBase<OptionType>>) => {
    const { data, selectOption, innerProps } = props;
    const isChecked = selectedOptions.includes(data.value);
    const onClick = () => {
      selectOption(data);
    };
    return (
      <components.Option
        {...props}
        innerProps={{
          ...innerProps,
          onClick: onClick,
        }}
      >
        <div className="ml-5 flex flex-row items-center gap-2">
          <CustomCheckbox isIndeterminate={false} isChecked={isChecked} onClick={onClick} />
          {props.children}
        </div>
      </components.Option>
    );
  };

  const CustomGroupHeading = (props: GroupHeadingProps<OptionType, boolean, GroupBase<OptionType>>) => {
    const { label: group, options } = props.data;

    const indeterminate = options.some((opt) => optionIsSelected(opt, group || ''));
    const isChecked = options.every((opt) => optionIsSelected(opt, group || ''));

    const onClick = () => {
      // if all options are already selected, deselect all
      if (isChecked) {
        deselectAllGroupOptions(options);
        return;
      }
      // Otherwise if indeterminate or false then add remaining values
      selectAllGroupOptions(options);
    };

    return (
      <components.GroupHeading {...props}>
        <div className="flex flex-row items-center gap-2">
          <CustomCheckbox isIndeterminate={indeterminate} isChecked={isChecked} onClick={onClick} />
          {props.children}
        </div>
      </components.GroupHeading>
    );
  };

  const value = !selectedOptions ? [] : options.filter((o) => selectedOptions.includes(o.value));

  const onChange = (selectedOption: OptionType | readonly OptionType[] | null) => {
    if (!selectedOption) {
      handleChange([]);
      return;
    }

    if (Array.isArray(selectedOption)) {
      handleChange(selectedOption.map((x) => x.value));
      return;
    }

    if ('value' in selectedOption) {
      handleChange([selectedOption.value]);
      return;
    }

    handleChange([]);
  };

  return (
    <Select<OptionType, boolean>
      isMulti
      /** Force re-render when options are loaded to update defaultValue. */
      key={key}
      name={name}
      value={value}
      options={groupedOptions}
      isLoading={isLoading}
      isClearable={isClearable}
      onChange={(selectedOption) => onChange(selectedOption)}
      styles={customStyles}
      placeholder={placeholder}
      noOptionsMessage={() => noOptionsMessage}
      components={{ Option: CustomOption, GroupHeading: CustomGroupHeading }}
      hideSelectedOptions={hideSelectedOptions}
    />
  );
};

export default GroupedSelect;
